/**
 * An on/off switch, which this library was missing and three files had each built for themselves.
 *
 * `h-5 w-9` track, `h-3.5 w-3.5` thumb, `translate-x-4` when on — written out three times, in the
 * test bar's Continuous toggle, the accessibility panel's scope switch and the sensor strip's
 * Device/Simulate. They had already drifted: the sensor one's track stayed primary-tinted whatever
 * state it was in, so the only thing that moved was the thumb, while the other two swapped the
 * track's colour as well.
 *
 * Nothing here is new design. It is the majority spelling of the three, made the only one.
 *
 * WHY THIS IS NOT `Button pressed`
 *
 * `Button`'s `pressed` is a button that stays down — a toolbar toggle whose label says what it
 * does. A switch is a different control with a different role, and a reader announces it
 * differently ("switch, on" rather than "button, pressed"). The two are not interchangeable and
 * neither should be spelled with the other.
 */

/**
 * The track and thumb alone, for a switch whose CONTROL is an ancestor.
 *
 * The accessibility panel's scope toggle is one button spanning a label, the switch and a second
 * label, so the switch itself must not be a button — a nested one is invalid HTML, and a second
 * focus stop for one control is worse than the markup problem. `aria-hidden`, because the ancestor
 * already carries `role='switch'` and its state.
 */
export function SwitchTrack({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden='true'
      className={`flex h-5 w-9 shrink-0 items-center rounded-full border p-0.5 transition-colors ${
        on ? 'border-ws-primary bg-ws-primary/20' : 'border-ws-border bg-ws-btn'}`}
    >
      <span
        className={`h-3.5 w-3.5 rounded-full shadow transition-transform ${
          on ? 'translate-x-4 bg-ws-primary' : 'translate-x-0 bg-ws-muted'}`}
      />
    </span>
  );
}

export function Switch({ on, onChange, label, states, disabled, onClick, className = '' }: {
  on: boolean;
  onChange: (next: boolean) => void;
  /** Always required. A switch has no text of its own, so without this it announces as nothing. */
  label: string;
  /**
   * The two state names, written either side of the track and INSIDE the control.
   *
   * A switch whose states are not obvious needs them shown — "Your Content / With Interactives"
   * says nothing as a bare toggle. Inside rather than beside, which is the part worth having: the
   * whole row becomes one 44px target instead of a 20px track with two inert captions next to it.
   * Both are `aria-hidden`, because `label` already says the same thing to a listener and the
   * switch's own state is in `aria-checked`.
   */
  states?: { off: string; on: string };
  disabled?: boolean;
  /**
   * For a switch inside something else clickable — the test bar's sits on a card whose own click
   * opens the editor, so the press must not also do that. Runs before `onChange`.
   */
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
}) {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => { onClick?.(e); onChange(!on); }}
      className={`${states
        ? 'flex min-h-11 items-center justify-center gap-2 rounded-lg px-2 hover:bg-ws-hover'
        : 'rounded-full'} focus-visible:[outline:2px_auto] [&:focus:not(:focus-visible)]:outline-none `
        + `disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {states && (
        <span aria-hidden='true' className={`text-[11px] ${on ? 'text-ws-muted' : 'font-medium text-ws-text'}`}>
          {states.off}
        </span>
      )}
      <SwitchTrack on={on} />
      {states && (
        <span aria-hidden='true' className={`text-[11px] ${on ? 'font-medium text-ws-text' : 'text-ws-muted'}`}>
          {states.on}
        </span>
      )}
    </button>
  );
}
